// File path: src/data/manufacturingKnowledge.js
// Purpose: Shared manufacturing data layer for ManuCalc.
// Used by: Job Builder, ManuPlan, Operation Builder, Risk Engine, Quote Engine, Reports.
import {
  MANUFACTURING_MATERIAL_OPTIONS,
  MATERIAL_GROUPS,
} from "./machiningMaterials";

// Archived only for migration review. Runtime material selectors, properties,
// risk, notes, and cutting data do not read this table.
export const LEGACY_MATERIAL_METADATA = [
  {
    id: "aluminum-6061",
    name: "Aluminum 6061",
    family: "Aluminum",
    machinability: "High",
    riskModifier: 0,
    defaultSfm: { hss: 250, carbide: 600 },
    notes: "Excellent general-purpose aluminum. Machines cleanly with sharp tools, proper chip evacuation, and reasonable feeds.",
  },
  {
    id: "aluminum-7075",
    name: "Aluminum 7075",
    family: "Aluminum",
    machinability: "High",
    riskModifier: 1,
    defaultSfm: { hss: 200, carbide: 500 },
    notes: "Strong aluminum alloy. Machines well but is less forgiving than 6061 for finish and tool pressure.",
  },
  {
    id: "aluminum-cast",
    name: "Cast Aluminum",
    family: "Aluminum",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 180, carbide: 450 },
    notes: "Porosity and inclusions can affect finish and cutter life. Verify casting quality before finish passes.",
  },
  {
    id: "mild-steel",
    name: "Mild Steel",
    family: "Steel",
    machinability: "Medium",
    riskModifier: 1,
    defaultSfm: { hss: 90, carbide: 350 },
    notes: "Common shop material. Requires attention to rigidity, tool condition, chip control, and coolant.",
  },
  {
    id: "cold-rolled-steel",
    name: "Cold Rolled Steel",
    family: "Steel",
    machinability: "Medium",
    riskModifier: 1,
    defaultSfm: { hss: 90, carbide: 350 },
    notes: "Common cold-finished low-carbon steel, often similar to 1018. Watch built-up edge, burrs, and finish variation.",
  },
  {
    id: "hot-rolled-steel",
    name: "Hot Rolled Steel",
    family: "Steel",
    machinability: "Medium",
    riskModifier: 1,
    defaultSfm: { hss: 80, carbide: 300 },
    notes: "Common hot-rolled mild steel. Mill scale and surface condition can reduce tool life and affect finish.",
  },
  {
    id: "steel-1018",
    name: "Steel 1018",
    family: "Steel",
    machinability: "Medium",
    riskModifier: 1,
    defaultSfm: { hss: 90, carbide: 350 },
    notes: "Common low-carbon steel. Watch built-up edge, finish variation, and burrs.",
  },
  {
    id: "steel-1045",
    name: "Steel 1045",
    family: "Steel",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 75, carbide: 300 },
    notes: "Medium-carbon steel. Higher cutting pressure than 1018 and benefits from rigid setups.",
  },
  {
    id: "steel-4140",
    name: "Steel 4140",
    family: "Alloy Steel",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 65, carbide: 260 },
    notes: "Alloy steel with higher strength. Watch heat, tool wear, and rigidity.",
  },
  {
    id: "steel-4140-prehard",
    name: "Steel 4140 Pre-Hard",
    family: "Alloy Steel",
    machinability: "Low",
    riskModifier: 3,
    defaultSfm: { hss: 45, carbide: 180 },
    notes: "Harder alloy steel. Use rigid workholding, conservative engagement, and good cutter condition.",
  },
  {
    id: "tool-steel-a2",
    name: "Tool Steel A2",
    family: "Tool Steel",
    machinability: "Low",
    riskModifier: 4,
    defaultSfm: { hss: 40, carbide: 150 },
    notes: "Air-hardening tool steel. Demands careful heat control, cutter selection, and setup rigidity.",
  },
  {
    id: "tool-steel-d2",
    name: "Tool Steel D2",
    family: "Tool Steel",
    machinability: "Very Low",
    riskModifier: 5,
    defaultSfm: { hss: 30, carbide: 120 },
    notes: "Abrasive tool steel. High tool-wear risk and often better suited to grinding or EDM for precision features.",
  },
  {
    id: "tool-steel-o1",
    name: "Tool Steel O1",
    family: "Tool Steel",
    machinability: "Medium",
    riskModifier: 3,
    defaultSfm: { hss: 50, carbide: 180 },
    notes: "Oil-hardening tool steel. Machines reasonably before heat treat but still needs rigid setups.",
  },
  {
    id: "spring-steel-1095",
    name: "1095 Spring Steel",
    family: "Spring Steel",
    machinability: "Low",
    riskModifier: 4,
    defaultSfm: { hss: 30, carbide: 110 },
    notes: "High-carbon spring steel. Machinability changes dramatically between annealed and hardened spring temper; verify hardness and supplier condition before choosing cutting data.",
  },
  {
    id: "spring-steel-5160",
    name: "5160 Spring Steel",
    family: "Spring Steel",
    machinability: "Low",
    riskModifier: 4,
    defaultSfm: { hss: 35, carbide: 130 },
    notes: "Chromium spring steel used in leaf springs and high-deflection parts. Heat-treated condition drives tool wear and setup risk.",
  },
  {
    id: "spring-steel-6150",
    name: "6150 Chrome-Vanadium Spring Steel",
    family: "Spring Steel",
    machinability: "Low",
    riskModifier: 4,
    defaultSfm: { hss: 30, carbide: 120 },
    notes: "Chrome-vanadium spring steel. Use conservative data unless the material is confirmed annealed and stable.",
  },
  {
    id: "stainless-spring-17-7",
    name: "17-7 PH Stainless Spring Steel",
    family: "Spring Steel",
    machinability: "Very Low",
    riskModifier: 5,
    defaultSfm: { hss: 25, carbide: 95 },
    notes: "Precipitation-hardening stainless spring material. Confirm condition and hardness such as CH900 or RH950 before machining or quoting.",
  },
  {
    id: "music-wire-astm-a228",
    name: "Music Wire / ASTM A228",
    family: "Spring Steel",
    machinability: "Very Low",
    riskModifier: 5,
    defaultSfm: { hss: 20, carbide: 80 },
    notes: "Cold drawn high-carbon spring wire. Use supplier data by wire diameter; do not assume annealed bar stock behavior.",
  },
  {
    id: "stainless-303",
    name: "Stainless 303",
    family: "Stainless Steel",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 60, carbide: 240 },
    notes: "Free-machining stainless. Easier than 304/316 but still benefits from sharp tools and chip control.",
  },
  {
    id: "stainless-304",
    name: "Stainless 304",
    family: "Stainless Steel",
    machinability: "Low",
    riskModifier: 3,
    defaultSfm: { hss: 45, carbide: 180 },
    notes: "Work-hardening risk. Avoid rubbing, use sharp tooling, maintain chip load, and manage heat carefully.",
  },
  {
    id: "stainless-316",
    name: "Stainless 316",
    family: "Stainless Steel",
    machinability: "Low",
    riskModifier: 4,
    defaultSfm: { hss: 40, carbide: 160 },
    notes: "Tough stainless. High risk of work hardening, tool wear, heat buildup, and poor finish without rigidity.",
  },
  {
    id: "stainless-17-4",
    name: "Stainless 17-4 PH",
    family: "Stainless Steel",
    machinability: "Low",
    riskModifier: 4,
    defaultSfm: { hss: 45, carbide: 175 },
    notes: "Precipitation-hardening stainless. Condition matters greatly. Watch tool wear and heat.",
  },
  {
    id: "cast-iron-gray",
    name: "Gray Cast Iron",
    family: "Cast Iron",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 70, carbide: 300 },
    notes: "Abrasive but stable. Dust control, machine protection, and tool wear monitoring matter.",
  },
  {
    id: "ductile-iron",
    name: "Ductile Iron",
    family: "Cast Iron",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 65, carbide: 260 },
    notes: "Stronger than gray iron. Watch tool pressure and insert wear.",
  },
  {
    id: "titanium-6al4v",
    name: "Titanium 6Al-4V",
    family: "Titanium",
    machinability: "Very Low",
    riskModifier: 5,
    defaultSfm: { hss: 30, carbide: 120 },
    notes: "Heat-sensitive and demanding. Requires conservative parameters, rigid workholding, sharp tooling, and strong coolant strategy.",
  },
  {
    id: "brass-360",
    name: "Brass 360",
    family: "Brass",
    machinability: "High",
    riskModifier: 0,
    defaultSfm: { hss: 250, carbide: 700 },
    notes: "Machines easily but can grab with aggressive tools. Watch burrs and use geometry appropriate for brass.",
  },
  {
    id: "bronze-932",
    name: "Bronze 932",
    family: "Bronze",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 90, carbide: 300 },
    notes: "Bearing bronze. Can be abrasive and gummy depending on grade. Use sharp tools and good chip control.",
  },
  {
    id: "copper-c110",
    name: "Copper C110",
    family: "Copper",
    machinability: "Low",
    riskModifier: 3,
    defaultSfm: { hss: 80, carbide: 250 },
    notes: "Gummy and heat-conductive. Sharp tools, polished flutes, and stable workholding help avoid poor finish.",
  },
  {
    id: "delrin-acetal",
    name: "Delrin / Acetal",
    family: "Plastic",
    machinability: "Medium",
    riskModifier: 1,
    defaultSfm: { hss: 250, carbide: 600 },
    notes: "Clamp gently. Plastic parts can deflect, melt, or move with heat and excessive clamping pressure.",
  },
  {
    id: "nylon",
    name: "Nylon",
    family: "Plastic",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 200, carbide: 500 },
    notes: "Flexible and moisture-sensitive. Control clamping force, heat, and burrs.",
  },
  {
    id: "uhmw",
    name: "UHMW",
    family: "Plastic",
    machinability: "Medium",
    riskModifier: 2,
    defaultSfm: { hss: 200, carbide: 500 },
    notes: "Very flexible plastic. Workholding and sharp tooling matter more than aggressive cutting data.",
  },
  {
    id: "peek",
    name: "PEEK",
    family: "Plastic",
    machinability: "Medium",
    riskModifier: 3,
    defaultSfm: { hss: 150, carbide: 350 },
    notes: "High-performance plastic. Expensive material, so fixturing, burr control, and heat control matter.",
  },
];

// Compatibility aliases preserve existing saved-plan IDs while all material
// properties continue to come from the global material database.
const SAVED_PLAN_ID_BY_GLOBAL_ID = {
  aluminum_6061: "aluminum-6061",
  aluminum_7075: "aluminum-7075",
  aluminum_cast: "aluminum-cast",
  mild_steel: "mild-steel",
  cold_rolled_steel: "cold-rolled-steel",
  hot_rolled_steel: "hot-rolled-steel",
  steel_1018: "steel-1018",
  steel_1045: "steel-1045",
  steel_4140_annealed: "steel-4140",
  steel_4140_ph: "steel-4140-prehard",
  tool_steel_a2: "tool-steel-a2",
  tool_steel_d2: "tool-steel-d2",
  tool_steel_o1: "tool-steel-o1",
  spring_steel_1095: "spring-steel-1095",
  spring_steel_5160: "spring-steel-5160",
  spring_steel_6150: "spring-steel-6150",
  spring_stainless_17_7: "stainless-spring-17-7",
  music_wire_astm_a228: "music-wire-astm-a228",
  stainless_303: "stainless-303",
  stainless_304: "stainless-304",
  stainless_316: "stainless-316",
  stainless_17_4: "stainless-17-4",
  cast_iron: "cast-iron-gray",
  ductile_iron: "ductile-iron",
  titanium_6al4v: "titanium-6al4v",
  brass: "brass-360",
  bronze: "bronze-932",
  copper: "copper-c110",
  acetal: "delrin-acetal",
  nylon: "nylon",
  hdpe: "uhmw",
};

const riskFromMachinability = (value) => {
  const numeric = Number(value || 50);
  if (numeric >= 80) return 0;
  if (numeric >= 60) return 1;
  if (numeric >= 40) return 2;
  if (numeric >= 25) return 3;
  if (numeric >= 15) return 4;
  return 5;
};

const machinabilityLabel = (value) => {
  const numeric = Number(value || 50);
  if (numeric >= 80) return "High";
  if (numeric >= 50) return "Medium";
  if (numeric >= 30) return "Low";
  return "Very Low";
};

export const MATERIALS = MANUFACTURING_MATERIAL_OPTIONS.map((material) => {
  const savedPlanId =
    SAVED_PLAN_ID_BY_GLOBAL_ID[material.id] || material.id.replace(/_/g, "-");
  return {
    id: savedPlanId,
    globalMaterialId: material.id,
    name: material.label,
    family: MATERIAL_GROUPS[material.group] || material.group,
    machinability: machinabilityLabel(material.machinability),
    machinabilityRating: material.machinability,
    riskModifier: riskFromMachinability(material.machinability),
    defaultSfm: {
      hss: Math.round(material.sfm * 0.35),
      carbide: material.sfm,
    },
    densityLbIn3: material.densityLbIn3,
    thermalExpansionPpmC: material.thermalExpansionPpmC,
    hardnessRockwell: material.hardnessRockwell,
    weldability: material.weldability || "Verify alloy, condition, process, and qualified procedure.",
    castingShrinkagePercent: material.castingShrinkagePercent,
    notes: material.heatTreatmentNotes || material.sourceNotes || "",
    searchTags: material.searchKeywords || [],
  };
});

export const MACHINE_TYPES = [
  {
    id: "cnc-mill-3-axis",
    name: "CNC Mill — 3 Axis",
    category: "CNC",
    strengths: ["2.5D features", "Pockets", "Slots", "Hole patterns", "Accurate repeat work"],
    limitations: ["Undercuts require special tooling or additional setups", "Side features may require a second operation"],
  },
  {
    id: "cnc-mill-4-axis",
    name: "CNC Mill — 4 Axis",
    category: "CNC",
    strengths: ["Indexed side features", "Reduced setup count", "Rotary positioning"],
    limitations: ["Requires good centerline/datum planning", "Not always worth setup time for simple parts"],
  },
  {
    id: "cnc-mill-5-axis",
    name: "CNC Mill — 5 Axis",
    category: "CNC",
    strengths: ["Complex 3D geometry", "Multi-side access", "Reduced fixturing", "Advanced surfacing"],
    limitations: ["Higher programming complexity", "More expensive machine time"],
  },
  {
    id: "vmc",
    name: "Vertical Machining Center",
    category: "CNC",
    strengths: ["General CNC milling", "Production work", "Rigid setups", "Toolchanger workflows"],
    limitations: ["Part access depends on setup strategy", "Multiple sides require additional setups or rotary work"],
  },
  {
    id: "hmc",
    name: "Horizontal Machining Center",
    category: "CNC",
    strengths: ["Production machining", "Chip evacuation", "Tombstone fixturing", "Multi-side access"],
    limitations: ["Higher setup complexity", "Fixture planning is critical"],
  },
  {
    id: "manual-mill",
    name: "Manual Mill",
    category: "Manual",
    strengths: ["Simple features", "One-off work", "Repair work", "Manual fitting"],
    limitations: ["Slow for pocket-heavy work", "Repeatability depends heavily on operator process"],
  },
  {
    id: "cnc-lathe",
    name: "CNC Lathe",
    category: "CNC",
    strengths: ["Round parts", "OD turning", "ID boring", "Threading", "Production repeatability"],
    limitations: ["Milled features require live tooling or secondary mill operation"],
  },
  {
    id: "cnc-lathe-live-tooling",
    name: "CNC Lathe — Live Tooling",
    category: "CNC",
    strengths: ["Turned parts", "Cross holes", "Flats", "Slots", "Reduced secondary ops"],
    limitations: ["Limited milling rigidity compared with a mill", "Programming and setup are more complex"],
  },
  {
    id: "swiss-lathe",
    name: "Swiss Lathe",
    category: "CNC",
    strengths: ["Small precision turned parts", "Long slender parts", "Production work", "Complex small components"],
    limitations: ["Best for smaller diameters", "Setup and programming are specialized"],
  },
  {
    id: "manual-lathe",
    name: "Manual Lathe",
    category: "Manual",
    strengths: ["Shafts", "Bushings", "Simple turned parts", "Repair work"],
    limitations: ["Slow for complex profiles", "Repeatability depends on operator skill"],
  },
  {
    id: "mill-turn",
    name: "Mill-Turn",
    category: "CNC",
    strengths: ["Turned parts with milled features", "Reduced handling", "Complex complete machining"],
    limitations: ["Programming complexity", "Higher setup planning demand"],
  },
  {
    id: "wire-edm",
    name: "Wire EDM",
    category: "CNC",
    strengths: ["Hard materials", "Sharp internal profiles", "Precision contours", "Thin slots"],
    limitations: ["Requires through-cut access", "Slow compared with milling", "Not for blind pockets"],
  },
  {
    id: "sinker-edm",
    name: "Sinker EDM",
    category: "CNC",
    strengths: ["Blind cavities", "Hard material details", "Fine internal features"],
    limitations: ["Electrode design required", "Slower process", "Specialized setup"],
  },
  {
    id: "surface-grinder",
    name: "Surface Grinder",
    category: "Finishing",
    strengths: ["Flatness", "Parallelism", "Thickness control", "Fine finishes"],
    limitations: ["Mostly flat surfaces", "Material must be held safely", "Often requires prior machining"],
  },
  {
    id: "cylindrical-grinder",
    name: "Cylindrical Grinder",
    category: "Finishing",
    strengths: ["Precision OD", "Roundness", "Bearing fits", "Shaft finishing"],
    limitations: ["Round/cylindrical work only", "Centerline datum planning required"],
  },
  {
    id: "centerless-grinder",
    name: "Centerless Grinder",
    category: "Finishing",
    strengths: ["High-volume round parts", "Consistent OD", "No centers required"],
    limitations: ["Specialized setup", "Best for simple cylindrical geometry"],
  },
  {
    id: "jig-grinder",
    name: "Jig Grinder",
    category: "Finishing",
    strengths: ["Precision holes", "Critical location work", "Toolroom precision"],
    limitations: ["Specialized precision process", "Slower than conventional machining"],
  },
  {
    id: "drill-press",
    name: "Drill Press",
    category: "Manual",
    strengths: ["Simple holes", "Low-cost drilling", "Shop utility work"],
    limitations: ["Poor positional control compared with CNC", "Work must be clamped securely"],
  },
  {
    id: "radial-arm-drill",
    name: "Radial Arm Drill",
    category: "Manual",
    strengths: ["Large workpieces", "Manual drilling", "Heavy plate and weldments"],
    limitations: ["Limited precision compared with CNC", "Setup and clamping are critical"],
  },
  {
    id: "broaching-machine",
    name: "Broaching Machine",
    category: "Specialty",
    strengths: ["Keyways", "Splines", "Internal profiles", "Repeat production features"],
    limitations: ["Requires broach tooling", "Feature-specific process"],
  },
  {
    id: "honing-machine",
    name: "Honing Machine",
    category: "Finishing",
    strengths: ["Precision bores", "Surface finish", "Roundness correction"],
    limitations: ["Bore finishing only", "Requires prior bore preparation"],
  },
  {
    id: "lapping-machine",
    name: "Lapping Machine",
    category: "Finishing",
    strengths: ["Extreme flatness", "Fine surface finish", "Precision mating surfaces"],
    limitations: ["Slow finishing process", "Requires controlled process and cleanliness"],
  },
  {
    id: "saw-horizontal-band",
    name: "Horizontal Band Saw",
    category: "Sawing",
    strengths: ["Cutting stock", "Blank preparation", "Efficient bar and plate cuts"],
    limitations: ["Not a finish process", "Saw cut accuracy and squareness vary"],
  },
  {
    id: "cold-saw",
    name: "Cold Saw",
    category: "Sawing",
    strengths: ["Accurate cutoffs", "Cleaner sawed ends", "Tube/bar cutting"],
    limitations: ["Size capacity limits", "Still usually needs machining for precision datums"],
  },
  {
    id: "waterjet",
    name: "Waterjet",
    category: "CNC",
    strengths: ["Flat profiles", "Plate cutting", "No heat affected zone", "Rough blanks"],
    limitations: ["Taper risk", "Edge finish varies", "Usually needs secondary machining for precision"],
  },
  {
    id: "laser-cutter",
    name: "Laser Cutter",
    category: "CNC",
    strengths: ["Sheet profiles", "Fast cutting", "Repeatable flat parts"],
    limitations: ["Heat affected zone", "Material/thickness limits", "Precision features may need machining"],
  },
  {
    id: "plasma-cutter",
    name: "CNC Plasma Cutter",
    category: "CNC",
    strengths: ["Plate cutting", "Fast rough profiles", "Fabrication blanks"],
    limitations: ["Heat affected zone", "Kerf taper", "Usually needs secondary machining for precision"],
  },
  {
    id: "press-brake",
    name: "Press Brake",
    category: "Fabrication",
    strengths: ["Sheet metal bends", "Formed brackets", "Production bending"],
    limitations: ["Requires bend allowance planning", "Tooling and material springback affect accuracy"],
  },
  {
    id: "manual-welder",
    name: "Welding Station",
    category: "Fabrication",
    strengths: ["Fabricated assemblies", "Repair work", "Frames", "Brackets"],
    limitations: ["Heat distortion", "Fit-up and fixturing matter heavily"],
  },
  {
    id: "coordinate-measuring-machine",
    name: "CMM",
    category: "Inspection",
    strengths: ["Dimensional inspection", "Datum verification", "Complex part inspection"],
    limitations: ["Inspection only", "Requires clean datums and proper programming"],
  },
  {
    id: "optical-comparator",
    name: "Optical Comparator",
    category: "Inspection",
    strengths: ["Profile inspection", "Small part inspection", "Toolroom verification"],
    limitations: ["Inspection only", "Best for 2D profiles and edges"],
  },
];

export const OPERATION_TYPES = [
  {
    id: "facing",
    name: "Facing / Squaring",
    compatibleFamilies: ["Milling", "Turning"],
    riskTags: ["datum", "flatness", "stock-cleanup"],
  },
  {
    id: "pocketing",
    name: "Pocketing",
    compatibleFamilies: ["Milling"],
    riskTags: ["chip-evacuation", "tool-reach", "corner-radius", "deflection"],
  },
  {
    id: "slotting",
    name: "Slotting",
    compatibleFamilies: ["Milling", "EDM"],
    riskTags: ["chip-packing", "tool-load", "width-control"],
  },
  {
    id: "drilling",
    name: "Drilling",
    compatibleFamilies: ["Milling", "Drilling", "Turning"],
    riskTags: ["hole-position", "drill-walk", "breakthrough-burr"],
  },
  {
    id: "tapping",
    name: "Tapping / Threading",
    compatibleFamilies: ["Milling", "Turning", "Drilling"],
    riskTags: ["broken-tap", "thread-quality", "late-stage-scrap"],
  },
  {
    id: "boring",
    name: "Boring",
    compatibleFamilies: ["Milling", "Turning"],
    riskTags: ["roundness", "tool-deflection", "finish"],
  },
  {
    id: "turning-od",
    name: "OD Turning",
    compatibleFamilies: ["Turning", "Hybrid"],
    riskTags: ["stickout", "chatter", "diameter-control"],
  },
  {
    id: "turning-id",
    name: "ID Boring / Internal Turning",
    compatibleFamilies: ["Turning", "Hybrid"],
    riskTags: ["bar-deflection", "chip-control", "finish"],
  },
  {
    id: "surface-grinding",
    name: "Surface Grinding",
    compatibleFamilies: ["Grinding"],
    riskTags: ["flatness", "parallelism", "heat", "magnetic-holding"],
  },
  {
    id: "wire-edm-profile",
    name: "Wire EDM Profile",
    compatibleFamilies: ["EDM"],
    riskTags: ["wire-path", "slug-control", "datum-transfer"],
  },
];

export const TOLERANCE_CLASSES = [
  {
    id: "general",
    name: "Clearance Fit (+0.002/+0.010 in)",
    description: "Free assembly fit where the mating part should assemble without force. Use the print/company standard when the exact allowance is controlled.",
    riskModifier: 0,
  },
  {
    id: "precision",
    name: "Slip Fit (+0.0002/+0.0015 in)",
    description: "Close sliding or locating fit that needs controlled size, stable datums, and repeatable inspection.",
    riskModifier: 2,
  },
  {
    id: "critical",
    name: "Press Fit (-0.0005/-0.0030 in)",
    description: "Interference fit requiring controlled mating sizes, material/temperature review, and a defined assembly method.",
    riskModifier: 4,
  },
];

export const FINISH_REQUIREMENTS = [
  {
    id: "general",
    name: "General Finish",
    riskModifier: 0,
  },
  {
    id: "fine",
    name: "Fine Machined Finish",
    riskModifier: 1,
  },
  {
    id: "cosmetic-critical",
    name: "Cosmetic / Critical Finish",
    riskModifier: 2,
  },
  {
    id: "ground",
    name: "Ground Finish",
    riskModifier: 2,
  },
];

export const DEFAULT_JOB_TEMPLATE = {
  id: "",
  name: "New Manufacturing Job",
  customer: "",
  partNumber: "",
  revision: "",
  materialId: "aluminum-6061",
  quantity: 1,
  dimensions: {
    length: 6,
    width: 4,
    height: 2,
  },
  stock: {
    length: 6.5,
    width: 4.5,
    height: 2.25,
  },
  toleranceClassId: "general",
  finishRequirementId: "general",
  features: {
    holes: true,
    pockets: false,
    slots: false,
    tappedHoles: false,
    thinWalls: false,
    sideFeatures: false,
    undersideFeatures: false,
    complex3D: false,
    criticalFinish: false,
  },
  machineRoute: ["cnc-mill-3-axis"],
  operations: [],
  notes: "",
  createdAt: null,
  updatedAt: null,
};

export const getMaterialById = (materialId) =>
  MATERIALS.find((material) => material.id === materialId) || MATERIALS[0];

export const getMachineById = (machineId) =>
  MACHINE_TYPES.find((machine) => machine.id === machineId) || MACHINE_TYPES[0];

export const getToleranceClassById = (toleranceClassId) =>
  TOLERANCE_CLASSES.find((item) => item.id === toleranceClassId) || TOLERANCE_CLASSES[0];

export const getFinishRequirementById = (finishRequirementId) =>
  FINISH_REQUIREMENTS.find((item) => item.id === finishRequirementId) || FINISH_REQUIREMENTS[0];

export const createJobFromTemplate = (overrides = {}) => {
  const now = new Date().toISOString();

  return {
    ...DEFAULT_JOB_TEMPLATE,
    ...overrides,
    id: overrides.id || `job-${Date.now()}`,
    dimensions: {
      ...DEFAULT_JOB_TEMPLATE.dimensions,
      ...(overrides.dimensions || {}),
    },
    stock: {
      ...DEFAULT_JOB_TEMPLATE.stock,
      ...(overrides.stock || {}),
    },
    features: {
      ...DEFAULT_JOB_TEMPLATE.features,
      ...(overrides.features || {}),
    },
    operations: overrides.operations || [],
    createdAt: overrides.createdAt || now,
    updatedAt: now,
  };
};
